import { useCallback, useEffect, useRef, useState } from "react";
import { SCOPE_HEX } from "../dashboard-v2/shared/styles";
import { scopeLabels } from "../dashboard-v2/shared/scopeLabels";
import CountUp from "./CountUp";
import "./CarbonJourney.css";

const DURATION = 6000;

const CHAPTERS = [
  {
    key: "erfassen",
    label: "Erfassen",
    title: "Automatisch erfasst",
    text: "Betriebs-, Energie- und Materialdaten fliessen zusammen, der CO2-Fussabdruck entsteht ohne Excel-Arbeit.",
  },
  {
    key: "hotspots",
    label: "Hotspots",
    title: "Die grössten Posten sichtbar",
    text: "Die Emissionen werden nach Scopes und Unterpositionen aufgeschlüsselt, so sehen Sie, welche Energie- und Materialposten Ihren Fussabdruck treiben.",
  },
  {
    key: "massnahmen",
    label: "Massnahmen",
    title: "Massnahmen priorisiert",
    text: "Konkrete Schritte, sortiert nach Einsparpotenzial, inklusive Hinweis auf passende Förderungen.",
  },
  {
    key: "benchmark",
    label: "Vergleich",
    title: "Branche im Blick",
    text: "Sie sehen auf einen Blick, wo Sie im Vergleich zu anderen Carrosserie- und Lackierbetrieben stehen.",
  },
];

/* ---------- Kapitel-Visuals ---------- */

function CaptureVisual() {
  const sources = ["Strom", "Gas", "Lack", "Fahrzeuge", "Abfall"];
  return (
    <div className="cj-capture" aria-hidden="true">
      <ul className="cj-capture-sources">
        {sources.map((s, i) => (
          <li key={s} style={{ "--i": i }}>
            <span>{s}</span>
            <i />
          </li>
        ))}
      </ul>
      <div className="cj-capture-core">
        <svg viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="48" className="cj-ring-track" />
          <circle cx="60" cy="60" r="48" className="cj-ring-value" pathLength="1" />
        </svg>
        <div className="cj-capture-readout">
          <strong><CountUp to={27.1} decimals={1} /></strong>
          <small>t CO2e / Jahr</small>
        </div>
      </div>
    </div>
  );
}

// Unterpositionen und Scope-Farben wie im Dashboard (scopeLabels.js / SCOPE_HEX).
const HOTSPOTS = [
  { scope: "Scope 2", label: scopeLabels["Scope 2.1"], value: 9.1 },
  { scope: "Scope 1", label: scopeLabels["Scope 1.1"], value: 7.4 },
  { scope: "Scope 3", label: scopeLabels["Scope 3.1.2"], value: 5.2 },
  { scope: "Scope 1", label: scopeLabels["Scope 1.2"], value: 3.6 },
  { scope: "Scope 3", label: scopeLabels["Scope 3.5"], value: 1.8 },
];
const HOTSPOT_MAX = Math.max(...HOTSPOTS.map((h) => h.value));

function HotspotVisual() {
  return (
    <ul className="cj-loss" aria-hidden="true">
      {HOTSPOTS.map((h, i) => (
        <li
          key={h.label}
          style={{ "--i": i, "--v": `${(h.value / HOTSPOT_MAX) * 100}%`, "--c": SCOPE_HEX[h.scope] }}
        >
          <span className="cj-loss-label"><b /> {h.label}</span>
          <span className="cj-loss-note">{h.value.toFixed(1).replace(".", ",")} t</span>
          <span className="cj-loss-bar"><i /></span>
        </li>
      ))}
    </ul>
  );
}

function ActionVisual() {
  const actions = [
    { rank: "01", label: "Lackierkabine ersetzen", saving: "−9.2 t", funded: true },
    { rank: "02", label: "Abluft-Wärmerückgewinnung", saving: "−4.1 t", funded: true },
    { rank: "03", label: "LED & Steuerung Halle", saving: "−1.8 t", funded: false },
  ];
  return (
    <ul className="cj-actions" aria-hidden="true">
      {actions.map((a, i) => (
        <li key={a.rank} style={{ "--i": i }}>
          <span className="cj-actions-rank">{a.rank}</span>
          <span className="cj-actions-label">
            {a.label}
            {a.funded && <em>Förderung möglich</em>}
          </span>
          <span className="cj-actions-saving">{a.saving}</span>
        </li>
      ))}
    </ul>
  );
}

function BenchmarkVisual() {
  return (
    <div className="cj-benchmark" aria-hidden="true">
      <div className="cj-benchmark-scale">
        <div className="cj-benchmark-marker">
          <span>Ihr Betrieb</span>
          <i />
        </div>
        <div className="cj-benchmark-avg">
          <i />
          <span>Branchenschnitt</span>
        </div>
      </div>
      <div className="cj-benchmark-legend">
        <span>effizient</span>
        <span>intensiv</span>
      </div>
      <p className="cj-benchmark-readout">
        <strong><CountUp to={18} suffix=" %" /></strong> unter dem Durchschnitt
        vergleichbarer Betriebe
      </p>
    </div>
  );
}

const VISUALS = {
  erfassen: CaptureVisual,
  hotspots: HotspotVisual,
  massnahmen: ActionVisual,
  benchmark: BenchmarkVisual,
};

/* ---------- Bühne ---------- */

export default function CarbonJourney() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !window.IntersectionObserver) { setIsVisible(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0.25,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || paused) return undefined;
    const id = setTimeout(() => setIndex((i) => (i + 1) % CHAPTERS.length), DURATION);
    return () => clearTimeout(id);
  }, [isVisible, paused, index]);

  const select = useCallback((i) => {
    setIndex(i);
  }, []);

  const chapter = CHAPTERS[index];
  const Visual = VISUALS[chapter.key];

  return (
    <section
      ref={sectionRef}
      className={`cj${isVisible ? " cj-visible" : ""}${paused ? " cj-paused" : ""}`}
      aria-labelledby="cj-title"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="cj-head">
        <h3 id="cj-title">Erfassen. Erkennen. Handeln.</h3>
      </div>

      <div className="cj-tabs" role="tablist" aria-label="Schritte">
        {CHAPTERS.map((c, i) => (
          <button
            key={c.key}
            type="button"
            role="tab"
            aria-selected={i === index}
            className={`cj-tab${i === index ? " cj-tab-active" : ""}${i < index ? " cj-tab-done" : ""}`}
            onClick={() => select(i)}
          >
            <span className="cj-tab-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="cj-tab-label">{c.label}</span>
            <span className="cj-tab-rail">
              <i
                key={i === index ? `run-${index}` : undefined}
                style={i === index ? { animationDuration: `${DURATION}ms` } : undefined}
                className={i === index ? "cj-tab-fill" : ""}
              />
            </span>
          </button>
        ))}
      </div>

      <div className="cj-stage" key={chapter.key}>
        <div className="cj-copy">
          <h4>{chapter.title}</h4>
          <p>{chapter.text}</p>
        </div>
        <div className="cj-visual">
          <Visual />
        </div>
      </div>
    </section>
  );
}
